import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import moment from 'moment';
import { PageHeader, RevealPasswordModal } from '../components/common';
import { ROUTES } from '../constants';
import { marketerService, ApiError } from '../services';

const inputClass =
  'w-full px-3 py-2 border border-gray-300 rounded-md text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500';

const isZeroDate = value => !value || String(value).startsWith('0001-01-01');

// HTML date input wants YYYY-MM-DD; BE expects the same on save.
const formatBirthdateForInput = value => {
  if (!value || isZeroDate(value)) return '';
  const m = moment(value);
  return m.isValid() ? m.format('YYYY-MM-DD') : '';
};

// E.164 sanity: starts with + then 8–15 digits (loose; BE validates strict).
const E164_PATTERN = /^\+\d{8,15}$/;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const yearsBetween = (dobStr, ref = new Date()) => {
  if (!dobStr) return 0;
  const dob = new Date(dobStr);
  if (Number.isNaN(dob.getTime())) return 0;
  let age = ref.getFullYear() - dob.getFullYear();
  const m = ref.getMonth() - dob.getMonth();
  if (m < 0 || (m === 0 && ref.getDate() < dob.getDate())) age--;
  return age;
};

const formDataFromMarketer = m => ({
  firstName: (m?.firstName && m.firstName !== 'N/A' ? m.firstName : '') || '',
  lastName: (m?.lastName && m.lastName !== 'N/A' ? m.lastName : '') || '',
  phone: (m?.phone && m.phone !== 'N/A' ? m.phone : '') || '',
  email: (m?.email && m.email !== 'N/A' ? m.email : '') || '',
  dateOfBirth: formatBirthdateForInput(m?.dateOfBirth),
});

// Per release notes 4.2: only send fields the admin actually edited.
// `name` is sent as concat of firstName+lastName if either changed.
const buildPersonalInfoDiff = (current, original) => {
  const trim = v => String(v ?? '').trim();
  const diff = {};
  const curFirst = trim(current.firstName);
  const curLast = trim(current.lastName);
  const origFirst = trim(original.firstName);
  const origLast = trim(original.lastName);
  if (curFirst !== origFirst || curLast !== origLast) {
    diff.name = [curFirst, curLast].filter(Boolean).join(' ');
  }
  const curPhone = trim(current.phone);
  if (curPhone !== trim(original.phone)) diff.phone = curPhone;
  const curEmail = trim(current.email);
  if (curEmail !== trim(original.email)) diff.email = curEmail;
  const curDob = trim(current.dateOfBirth);
  if (curDob !== trim(original.dateOfBirth)) diff.dateOfBirth = curDob;
  return diff;
};

const MarketerEditPersonalPage = () => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const marketerFromState = location.state?.marketer;

  // Header label can render from route state immediately; form values must
  // come from a fresh fetch so we know the `revealed` flag is current.
  const [marketer, setMarketer] = useState(marketerFromState || null);
  const [form, setForm] = useState(null);
  const [originalForm, setOriginalForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [revealed, setRevealed] = useState(false);
  const [revealModalOpen, setRevealModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [savedOk, setSavedOk] = useState(false);
  const [error, setError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const initialFetchDoneRef = useRef(false);
  // RevealPasswordModal fires onSuccess THEN onClose on a successful verify;
  // this ref tells onClose to skip the bounce-back redirect in that case.
  const justVerifiedRef = useRef(false);

  const detailHref = id
    ? ROUTES.generatePath(ROUTES.MARKETERS_DETAIL_LIST, { id })
    : ROUTES.MARKETERS;

  const goBackToDetail = useCallback(() => {
    if (!id) {
      navigate(ROUTES.MARKETERS);
      return;
    }
    navigate(detailHref, { state: marketer ? { marketer } : undefined });
  }, [detailHref, id, marketer, navigate]);

  const loadMarketer = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const response = await marketerService.getFormattedMarketerDetails(id);
      const data = response?.data;
      if (!data) {
        setError('Failed to load creator details.');
        return;
      }
      setMarketer(data);
      const isRevealed = data.revealed === true;
      setRevealed(isRevealed);
      if (isRevealed) {
        const next = formDataFromMarketer(data);
        setForm(next);
        // Snapshot original values for diff-vs-original logic at save time.
        setOriginalForm(next);
      } else {
        // Don't pre-fill with masked values — wait for reveal then refetch.
        setForm(formDataFromMarketer({}));
        setOriginalForm(formDataFromMarketer({}));
        setRevealModalOpen(true);
      }
    } catch (err) {
      console.error('Failed to load creator:', err);
      setError(
        err instanceof ApiError
          ? err.message
          : 'Failed to load creator. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (initialFetchDoneRef.current) return;
    initialFetchDoneRef.current = true;
    loadMarketer();
  }, [loadMarketer]);

  const handleChange = field => e => {
    const nextValue = e.target.value;
    setForm(prev => ({ ...prev, [field]: nextValue }));
    setFieldErrors(prev => {
      if (!prev?.[field]) return prev;
      const trimmed = String(nextValue ?? '').trim();
      if (!trimmed) return prev;
      const { [field]: _ignored, ...rest } = prev;
      return rest;
    });
    if (savedOk) setSavedOk(false);
  };

  const validate = currentForm => {
    const next = {};
    if (!String(currentForm.firstName ?? '').trim()) {
      next.firstName = 'First Name is required.';
    }
    if (!String(currentForm.lastName ?? '').trim()) {
      next.lastName = 'Last Name is required.';
    }
    const phone = String(currentForm.phone ?? '').trim();
    if (!phone) {
      next.phone = 'Phone Number is required.';
    } else if (!E164_PATTERN.test(phone)) {
      next.phone = 'Phone must be in E.164 format (e.g. +6281234567890).';
    }
    const email = String(currentForm.email ?? '').trim();
    if (!email) {
      next.email = 'Email Address is required.';
    } else if (!EMAIL_PATTERN.test(email)) {
      next.email = 'Email format is invalid.';
    }
    const dob = String(currentForm.dateOfBirth ?? '').trim();
    if (!dob) {
      next.dateOfBirth = 'Birthdate is required.';
    } else if (yearsBetween(dob) < 18) {
      next.dateOfBirth = 'Creator must be at least 18 years old.';
    }
    return next;
  };

  const handleSubmit = e => {
    e.preventDefault();
    if (!id || !form) return;
    const nextErrors = validate(form);
    if (Object.keys(nextErrors).length > 0) {
      setFieldErrors(nextErrors);
      setError('Please fix the highlighted fields.');
      return;
    }
    // Skip BE round-trip when admin saves with no actual edits.
    const diff = buildPersonalInfoDiff(form, originalForm || {});
    if (Object.keys(diff).length === 0) {
      setError('No changes to save.');
      return;
    }
    setError(null);
    setShowConfirm(true);
  };

  const handleConfirmSave = async () => {
    if (!id || !form) return;
    setShowConfirm(false);
    setSaving(true);
    setError(null);
    try {
      const diff = buildPersonalInfoDiff(form, originalForm || {});
      // Always send skipVerificationReset: true so admin edits don't force
      // a re-verification flow on the user side (per product decision).
      const payload = { ...diff, skipVerificationReset: true };
      await marketerService.updatePersonalInfo(id, payload);
      setSavedOk(true);
      // Re-baseline original so subsequent edits diff against the just-saved
      // state, not the stale pre-edit values.
      setOriginalForm(form);
      // Mirror saved values into marketer cache so detail page (via Back to
      // Details) reflects the new values without a refetch.
      setMarketer(prev =>
        prev
          ? {
              ...prev,
              firstName: String(form.firstName).trim(),
              lastName: String(form.lastName).trim(),
              fullName: [
                String(form.firstName).trim(),
                String(form.lastName).trim(),
              ]
                .filter(Boolean)
                .join(' '),
              phone: String(form.phone).trim(),
              email: String(form.email).trim(),
              dateOfBirth: String(form.dateOfBirth).trim(),
            }
          : prev
      );
    } catch (err) {
      console.error('Failed to save personal info:', err);
      const code = err instanceof ApiError ? err.code : null;
      if (code === 'PhoneAlreadyUsed') {
        setFieldErrors(prev => ({
          ...prev,
          phone: 'Phone number is already used by another account.',
        }));
        setError('Phone number is already used by another account.');
      } else if (code === 'EmailAlreadyUsed') {
        setFieldErrors(prev => ({
          ...prev,
          email: 'Email is already used by another account.',
        }));
        setError('Email is already used by another account.');
      } else if (code === 'FeatureInvalidPayload') {
        setError(
          err.message ||
            'Validation failed. Please review phone, email, and birthdate.'
        );
      } else if (code === 'FeatureUnauthorized') {
        setError(
          'Your admin session has expired or lacks permission. Please re-verify and try again.'
        );
      } else if (code === 'DatabaseNotFound') {
        setError('Creator not found.');
      } else {
        setError(
          err instanceof ApiError
            ? err.message
            : 'Failed to save personal information. Please try again.'
        );
      }
    } finally {
      setSaving(false);
    }
  };

  const marketerLabel =
    marketer?.fullName ||
    [marketer?.firstName, marketer?.lastName].filter(Boolean).join(' ') ||
    'Creator';

  if (loading && !marketer) {
    return (
      <div className='flex items-center justify-center min-h-[40vh]'>
        <div className='flex items-center space-x-2'>
          <svg
            className='animate-spin h-8 w-8 text-blue-600'
            fill='none'
            viewBox='0 0 24 24'
          >
            <circle
              className='opacity-25'
              cx='12'
              cy='12'
              r='10'
              stroke='currentColor'
              strokeWidth='4'
            />
            <path
              className='opacity-75'
              fill='currentColor'
              d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
            />
          </svg>
          <span className='text-gray-600'>Loading…</span>
        </div>
      </div>
    );
  }

  const hasFieldError = key => !!fieldErrors?.[key];
  const inputClassFor = key =>
    `${inputClass} ${
      hasFieldError(key)
        ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
        : ''
    }`;

  const editable = revealed && !saving;

  return (
    <div className='space-y-6'>
      <PageHeader
        title='Edit Personal Information'
        subtitle="Edit the creator's information and make sure the data is correct"
        breadcrumbs={[
          { name: 'Home', href: ROUTES.DASHBOARD },
          { name: 'Creators', href: ROUTES.MARKETERS },
          { name: marketerLabel, href: detailHref, state: { marketer } },
          { name: 'Edit Personal Information' },
        ]}
      />

      {savedOk && (
        <div className='bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 flex flex-wrap items-center justify-between gap-3'>
          <p className='text-sm text-amber-800'>Personal information saved.</p>
          <button
            type='button'
            onClick={goBackToDetail}
            className='px-3 py-1.5 border border-amber-300 rounded-md text-xs font-medium text-amber-800 bg-white hover:bg-amber-50 whitespace-nowrap'
          >
            Back to Details
          </button>
        </div>
      )}

      {error && (
        <div className='bg-red-50 border border-red-200 rounded-lg p-4'>
          <p className='text-sm text-red-700'>{error}</p>
        </div>
      )}

      <form
        onSubmit={handleSubmit}
        className='bg-white rounded-lg shadow-sm border border-gray-200'
      >
        <div className='px-4 md:px-6 py-5 border-b border-gray-200'>
          <h3 className='text-lg font-semibold text-gray-900'>
            {marketerLabel}
          </h3>
          <p className='text-xs text-gray-500 mt-0.5 break-all'>
            Creator ID: {marketer?.id || '-'}
          </p>
        </div>

        <div className='p-6 space-y-6'>
          <div className='grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-5'>
            <div>
              <label
                htmlFor='firstName'
                className='block text-sm font-medium text-gray-900 mb-1'
              >
                First Name <span className='text-red-600'>*</span>
              </label>
              <input
                id='firstName'
                type='text'
                value={form?.firstName ?? ''}
                onChange={handleChange('firstName')}
                disabled={!editable}
                className={inputClassFor('firstName')}
                autoComplete='given-name'
                aria-invalid={hasFieldError('firstName')}
              />
              {hasFieldError('firstName') && (
                <p className='mt-1 text-xs text-red-600'>
                  {fieldErrors.firstName}
                </p>
              )}
            </div>
            <div>
              <label
                htmlFor='lastName'
                className='block text-sm font-medium text-gray-900 mb-1'
              >
                Last Name <span className='text-red-600'>*</span>
              </label>
              <input
                id='lastName'
                type='text'
                value={form?.lastName ?? ''}
                onChange={handleChange('lastName')}
                disabled={!editable}
                className={inputClassFor('lastName')}
                autoComplete='family-name'
                aria-invalid={hasFieldError('lastName')}
              />
              {hasFieldError('lastName') && (
                <p className='mt-1 text-xs text-red-600'>
                  {fieldErrors.lastName}
                </p>
              )}
            </div>
            <div>
              <label
                htmlFor='phone'
                className='block text-sm font-medium text-gray-900 mb-1'
              >
                Phone Number <span className='text-red-600'>*</span>
              </label>
              <input
                id='phone'
                type='tel'
                value={form?.phone ?? ''}
                onChange={handleChange('phone')}
                disabled={!editable}
                className={inputClassFor('phone')}
                autoComplete='tel'
                placeholder='+6281234567890'
                aria-invalid={hasFieldError('phone')}
              />
              {hasFieldError('phone') && (
                <p className='mt-1 text-xs text-red-600'>{fieldErrors.phone}</p>
              )}
            </div>
            <div>
              <label
                htmlFor='email'
                className='block text-sm font-medium text-gray-900 mb-1'
              >
                Email Address <span className='text-red-600'>*</span>
              </label>
              <input
                id='email'
                type='email'
                value={form?.email ?? ''}
                onChange={handleChange('email')}
                disabled={!editable}
                className={inputClassFor('email')}
                autoComplete='email'
                aria-invalid={hasFieldError('email')}
              />
              {hasFieldError('email') && (
                <p className='mt-1 text-xs text-red-600'>{fieldErrors.email}</p>
              )}
            </div>
            <div>
              <label
                htmlFor='dateOfBirth'
                className='block text-sm font-medium text-gray-900 mb-1'
              >
                Birthdate <span className='text-red-600'>*</span>
              </label>
              <input
                id='dateOfBirth'
                type='date'
                value={form?.dateOfBirth ?? ''}
                onChange={handleChange('dateOfBirth')}
                disabled={!editable}
                className={inputClassFor('dateOfBirth')}
                aria-invalid={hasFieldError('dateOfBirth')}
              />
              {hasFieldError('dateOfBirth') && (
                <p className='mt-1 text-xs text-red-600'>
                  {fieldErrors.dateOfBirth}
                </p>
              )}
            </div>
          </div>

          <div className='flex flex-wrap items-center justify-end gap-3 pt-2'>
            <button
              type='button'
              onClick={goBackToDetail}
              disabled={saving}
              className='px-5 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50'
            >
              Cancel
            </button>
            <button
              type='submit'
              disabled={!editable}
              className='px-5 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed'
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      </form>

      {showConfirm && (
        <div className='fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4'>
          <div className='bg-white rounded-lg shadow-xl w-full max-w-md p-6'>
            <h2 className='text-base font-semibold text-gray-900 mb-3'>
              Confirm Personal Information Update
            </h2>
            <p className='text-sm text-gray-600 mb-6'>
              Please confirm the update to the creator's personal information.
              Under GDPR, you are responsible for ensuring that the data is
              accurate and up to date. This action will be logged with your
              user ID and timestamp for audit purposes.
            </p>
            <div className='flex justify-end gap-3'>
              <button
                type='button'
                onClick={() => setShowConfirm(false)}
                className='px-5 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50'
              >
                Cancel
              </button>
              <button
                type='button'
                onClick={handleConfirmSave}
                className='px-5 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700'
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      <RevealPasswordModal
        isOpen={revealModalOpen}
        onClose={() => {
          setRevealModalOpen(false);
          if (justVerifiedRef.current) {
            justVerifiedRef.current = false;
            return;
          }
          if (!revealed) goBackToDetail();
        }}
        onSuccess={async () => {
          justVerifiedRef.current = true;
          await loadMarketer();
        }}
      />
    </div>
  );
};

export default MarketerEditPersonalPage;
